import { createSlice } from "@reduxjs/toolkit";
import LikeableType from "types/LikeableType";
import SessionType from "types/SessionType";
import UserType from "types/UserType";
import { likeableKey } from "./currentUserSelector";
import ListBookType from "types/ListBookType";

export type CurrentUserStateType = {
  followedListIds: number[];
  followedUsers: Record<string, number>;
  matchScores: Record<number, number>;
  likes: Record<string, boolean>;
  ownedEditions: Record<number, number>;
  pinnedPromptIds: number[];
  promptAnswers: Record<string, number>;
  session: SessionType;
  user: UserType | null;
  unfoundBookMatchScores: number[];
};

export const currentUserState: CurrentUserStateType = {
  followedListIds: [],
  followedUsers: {},
  likes: {},
  matchScores: {},
  ownedEditions: {},
  pinnedPromptIds: [],
  promptAnswers: {},
  session: null,
  user: null,
  unfoundBookMatchScores: [],
};

const currentUserSlice = createSlice({
  name: "CurrentUserSlice",
  initialState: currentUserState,
  reducers: {
    setSession(state, { payload }) {
      if (state.session !== payload) {
        state.session = payload;
      }
    },
    setUser(state, { payload }: { payload: UserType }) {
      state.user = payload;
    },
    setMatchScore(state, { payload: { bookId, score } }: { payload: { bookId: number, score: number } }) {
      const newScore = Math.round(score * 20);
      state.matchScores[bookId] = newScore;
    },
    setNotificationsCount(state, { payload }: { payload: number }) {
      if (state.user) {
        state.user.notificationsCount = payload;
      }
    },
    refreshAccessToken(
      state,
      { payload: { token, expiresAt } }: { payload: { token: string, expiresAt: number } }
    ) {
      state.session.jwt = token;
      state.session.expiresAt = expiresAt;
    },
    toggleLike(state, { payload: { likeable } }: { payload: { likeable: LikeableType } }) {
      const key = likeableKey(likeable)
      if (state.likes[key]) {
        delete state.likes[key];
      } else {
        state.likes[key] = true;
      }
    },
    deleteOwnedEdition(state, { payload: { editionId } }: { payload: { editionId: number } }) {
      delete state.ownedEditions[editionId];
    },
    addOwnedEdition(state, { payload: { listBook } }: { payload: { listBook: ListBookType } }) {
      state.ownedEditions[listBook.editionId] = listBook.id;
    },
    appendUnfoundBookMatchScores(state, { payload }: { payload: number[] }) {
      state.unfoundBookMatchScores = [
        ...state.unfoundBookMatchScores,
        ...payload,
      ];
    },
    deletePromptAnswer(state, { payload: { bookId, promptId } }: { payload: { bookId: number, promptId: number } }) {
      delete state.promptAnswers[`Prompt-${promptId}-${bookId}`];
    },
    addPromptAnswer(state, { payload: { bookId, promptId, answerId } }: { payload: { bookId: number, promptId: number, answerId: number } }) {
      state.promptAnswers[`Prompt-${promptId}-${bookId}`] = answerId;
    },
    unfollowUser(state, { payload: { userId } }: { payload: { userId: number } }) {
      // Followed only, or neither
      state.followedUsers[userId] = state.followedUsers[userId] === 7 ? 2 : 0; 
    },
    followUser(state, { payload: { userId } }: { payload: { userId: number } }) {
      // Followed only, don't know if they follow by at this point
      state.followedUsers[userId] = state.followedUsers[userId] === 2 ? 7 : 1; 
    },
    pinPrompt(state, { payload: { promptId } }: { payload: { promptId: number } }) {
      state.pinnedPromptIds.push(promptId);
    },
    unPinPrompt(state, { payload: { promptId } }: { payload: { promptId: number } }) {
      state.pinnedPromptIds = state.pinnedPromptIds.filter((id) => id !== promptId);
    },
  },
});

export const currentUserActions = currentUserSlice.actions;

export default currentUserSlice.reducer;

export const {
  setMatchScore, 
  setUser,
  toggleLike, 
  deleteOwnedEdition, 
  addOwnedEdition, 
  deletePromptAnswer, 
  addPromptAnswer,
  unfollowUser, 
  followUser,
  pinPrompt,
  unPinPrompt
} = currentUserSlice.actions;
